import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Card,
  Typography,
  Tag,
  Empty,
  Spin,
  message,
  Space,
  Tabs,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
} from 'antd';
import {
  Button as MobileButton,
  List,
  Tag as MobileTag,
  Empty as MobileEmpty,
  SpinLoading,
  Toast,
  Dialog,
} from 'antd-mobile';
import {
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined,
  BankOutlined,
  TagOutlined,
  EditOutlined,
  DeleteOutlined,
  LogoutOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import {
  getAllRecords,
  updateRecord,
  deleteRecord,
  getAuditLogs,
} from '../../db/database';
import { downloadExcel } from '../../utils/exportExcel';
import { getSizeInfo, SIZE_OPTIONS } from '../../data/sizeReference';
import { useAdmin } from '../../context/AdminContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import styles from './Records.module.css';

const { Title, Text } = Typography;

const GENDER_OPTIONS = [
  { label: '小哥哥', value: '小哥哥' },
  { label: '小姐姐', value: '小姐姐' },
];

const ACTION_META = {
  CREATE: { label: '新建', color: 'green' },
  RESUBMIT: { label: '重新申领', color: 'orange' },
  UPDATE: { label: '编辑', color: 'blue' },
  DELETE: { label: '删除', color: 'red' },
};

function RecordEditForm({ form, initialValues, onFinish, submitting }) {
  return (
    <Form form={form} layout="vertical" onFinish={onFinish} initialValues={initialValues} requiredMark={false}>
      <Form.Item label="姓名" name="name" rules={[{ required: true, message: '请输入姓名' }]}>
        <Input size="large" />
      </Form.Item>
      <Form.Item label="部门" name="department" rules={[{ required: true, message: '请输入部门' }]}>
        <Input size="large" />
      </Form.Item>
      <Form.Item label="性别" name="gender" rules={[{ required: true, message: '请选择性别' }]}>
        <Select size="large" options={GENDER_OPTIONS} />
      </Form.Item>
      <Form.Item label="尺码" name="size" rules={[{ required: true, message: '请选择尺码' }]}>
        <Select size="large" options={SIZE_OPTIONS.map((s) => ({ label: s, value: s }))} />
      </Form.Item>
      <Form.Item label="颜色" name="color">
        <Input size="large" />
      </Form.Item>
      <Form.Item label="备注" name="remark">
        <Input size="large" />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
          保存修改
        </Button>
      </Form.Item>
    </Form>
  );
}

export default function RecordsPage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { logout, adminUser } = useAdmin();
  const [form] = Form.useForm();
  const [records, setRecords] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('records');
  const [editOpen, setEditOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailText, setDetailText] = useState('');

  const operator = adminUser || 'superadmin';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllRecords(operator);
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRecords([]);
      message.error('加载数据失败，请确认后端服务已启动');
    } finally {
      setLoading(false);
    }
  }, [operator]);

  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await getAuditLogs(operator);
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAuditLogs([]);
      message.error('加载审计日志失败');
    } finally {
      setAuditLoading(false);
    }
  }, [operator]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (activeTab === 'audit') loadAuditLogs();
  }, [activeTab, loadAuditLogs]);

  const handleExport = async () => {
    try {
      await downloadExcel(operator);
      if (isMobile) {
        Toast.show({ icon: 'success', content: '导出成功' });
      } else {
        message.success('Excel 导出成功');
      }
    } catch (err) {
      console.error(err);
      message.error('导出失败');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      department: record.department,
      gender: record.gender,
      size: record.size,
      color: record.color || '',
      remark: record.remark || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSave = async (values) => {
    if (!editingRecord) return;
    setSaving(true);
    try {
      await updateRecord(editingRecord.id, values, operator);
      message.success('保存成功');
      closeEdit();
      loadRecords();
      if (activeTab === 'audit') loadAuditLogs();
    } catch (err) {
      message.error(err?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRecord(id, operator);
      if (isMobile) {
        Toast.show({ icon: 'success', content: '已删除' });
      } else {
        message.success('已删除');
      }
      loadRecords();
      if (activeTab === 'audit') loadAuditLogs();
    } catch (err) {
      const msg = err?.message || '删除失败';
      if (isMobile) Toast.show({ icon: 'fail', content: msg });
      else message.error(msg);
    }
  };

  const showDetail = (detail) => {
    try {
      const parsed = typeof detail === 'string' ? JSON.parse(detail) : detail;
      setDetailText(JSON.stringify(parsed, null, 2));
    } catch {
      setDetailText(String(detail || ''));
    }
    setDetailOpen(true);
  };

  const actionColumn = {
    title: '操作',
    key: 'actions',
    width: 120,
    fixed: 'right',
    render: (_, record) => (
      <Space size={4}>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          编辑
        </Button>
        <Popconfirm
          title="确认删除该申领记录？"
          description="删除后不可恢复，操作将记入审计日志。"
          onConfirm={() => handleDelete(record.id)}
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    ),
  };

  const recordColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '姓名', dataIndex: 'name', width: 100 },
    { title: '部门', dataIndex: 'department', width: 120 },
    {
      title: '性别',
      dataIndex: 'gender',
      width: 90,
      render: (g) => <Tag bordered={false}>{g}</Tag>,
    },
    {
      title: '尺码',
      dataIndex: 'size',
      width: 180,
      render: (size) => {
        const info = getSizeInfo(size);
        return (
          <span>
            <Tag color="blue" bordered={false}>{size}</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{info.friendlyName}</Text>
          </span>
        );
      },
    },
    { title: '颜色', dataIndex: 'color', width: 140, render: (c) => c || '—' },
    { title: '备注', dataIndex: 'remark', render: (r) => r || '—' },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 180,
      render: (t) => new Date(t).toLocaleString('zh-CN'),
    },
    actionColumn,
  ];

  const auditColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 170,
      render: (t) => new Date(t).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 80,
      render: (action) => {
        const meta = ACTION_META[action] || { label: action, color: 'default' };
        return <Tag color={meta.color} bordered={false}>{meta.label}</Tag>;
      },
    },
    { title: '操作人', dataIndex: 'operator', width: 110 },
    { title: '摘要', dataIndex: 'summary', ellipsis: true },
    {
      title: '详情',
      dataIndex: 'detail',
      width: 80,
      render: (detail) => (
        detail ? (
          <Button type="link" size="small" onClick={() => showDetail(detail)}>查看</Button>
        ) : '—'
      ),
    },
  ];

  const toolbar = (
    <Space wrap>
      <Button icon={<ReloadOutlined />} onClick={() => {
        loadRecords();
        if (activeTab === 'audit') loadAuditLogs();
      }}>
        刷新
      </Button>
      <Button
        type="primary"
        icon={<DownloadOutlined />}
        onClick={handleExport}
        disabled={records.length === 0}
      >
        导出 Excel
      </Button>
      <Button icon={<LogoutOutlined />} onClick={handleLogout}>退出管理</Button>
    </Space>
  );

  if (isMobile) {
    return (
      <section className="card">
        <div className={styles.toolbarMobile}>
          <Title level={5} className={styles.pageTitle}>
            <FileTextOutlined /> 福利登记管理
          </Title>
          <Space size={4}>
            <MobileButton size="mini" onClick={loadRecords}><ReloadOutlined /></MobileButton>
            <MobileButton size="mini" color="primary" onClick={handleExport}><DownloadOutlined /></MobileButton>
            <MobileButton size="mini" onClick={handleLogout}><LogoutOutlined /></MobileButton>
          </Space>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'records', label: '申领记录' },
            { key: 'audit', label: '审计日志' },
          ]}
          className={styles.tabs}
        />

        {activeTab === 'records' ? (
          loading ? (
            <div className={styles.loadingMobile}><SpinLoading color="primary" /></div>
          ) : records.length === 0 ? (
            <MobileEmpty description="暂无申领记录" />
          ) : (
            <List className={styles.listMobile}>
              {records.map((r) => {
                const info = getSizeInfo(r.size);
                return (
                  <List.Item key={r.id} className={styles.listItem}>
                    <div className={styles.recordCard}>
                      <div className={styles.recordHeader}>
                        <strong>{r.name}</strong>
                        <MobileTag>{r.gender}</MobileTag>
                      </div>
                      <div className={styles.recordBody}>
                        <span><BankOutlined /> {r.department}</span>
                        <span><TagOutlined /> {r.size} · {info.friendlyName}</span>
                        {r.color && <span>{r.color}</span>}
                        {r.remark && <span><EditOutlined /> {r.remark}</span>}
                        <span className={styles.time}>
                          {new Date(r.created_at).toLocaleString('zh-CN')}
                        </span>
                      </div>
                      <div className={styles.recordActions}>
                        <MobileButton size="mini" onClick={() => openEdit(r)}>编辑</MobileButton>
                        <MobileButton
                          size="mini"
                          color="danger"
                          onClick={() => {
                            Dialog.confirm({
                              content: '确认删除该申领记录？操作将记入审计日志。',
                              onConfirm: () => handleDelete(r.id),
                            });
                          }}
                        >
                          删除
                        </MobileButton>
                      </div>
                    </div>
                  </List.Item>
                );
              })}
            </List>
          )
        ) : auditLoading ? (
          <div className={styles.loadingMobile}><SpinLoading color="primary" /></div>
        ) : auditLogs.length === 0 ? (
          <MobileEmpty description="暂无审计日志" />
        ) : (
          <List className={styles.listMobile}>
            {auditLogs.map((log) => {
              const meta = ACTION_META[log.action] || { label: log.action };
              return (
                <List.Item key={log.id} className={styles.listItem}>
                  <div className={styles.recordCard}>
                    <div className={styles.recordHeader}>
                      <MobileTag>{meta.label}</MobileTag>
                      <span className={styles.time}>{log.operator}</span>
                    </div>
                    <div className={styles.recordBody}>
                      <span>{log.summary}</span>
                      <span className={styles.time}>
                        {new Date(log.created_at).toLocaleString('zh-CN')}
                      </span>
                    </div>
                    {log.detail && (
                      <MobileButton size="mini" fill="outline" onClick={() => showDetail(log.detail)}>
                        查看详情
                      </MobileButton>
                    )}
                  </div>
                </List.Item>
              );
            })}
          </List>
        )}

        <Modal
          title={`编辑申领 #${editingRecord?.id || ''}`}
          open={editOpen}
          onCancel={closeEdit}
          footer={null}
          destroyOnClose
          centered
        >
          <RecordEditForm form={form} onFinish={handleSave} submitting={saving} />
        </Modal>

        <Modal
          title="操作详情"
          open={detailOpen}
          onCancel={() => setDetailOpen(false)}
          footer={null}
          width={560}
        >
          <pre className={styles.detailPre}>{detailText}</pre>
        </Modal>
      </section>
    );
  }

  return (
    <div className={styles.records}>
      <div className={styles.toolbar}>
        <div>
          <Title level={3} className={styles.pageTitle}>
            <FileTextOutlined /> 在途 · 福利登记管理
          </Title>
          <p className={styles.pageDesc}>
            超级管理员可编辑/删除申领记录，所有操作均记入审计日志
          </p>
        </div>
        {toolbar}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className={styles.tabs}
        items={[
          {
            key: 'records',
            label: '申领记录',
            children: (
              <Card className={styles.tableCard} bordered={false}>
                <Spin spinning={loading}>
                  {records.length === 0 && !loading ? (
                    <Empty description="暂无申领记录" />
                  ) : (
                    <Table
                      columns={recordColumns}
                      dataSource={records}
                      rowKey="id"
                      pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
                      scroll={{ x: 1100 }}
                    />
                  )}
                </Spin>
              </Card>
            ),
          },
          {
            key: 'audit',
            label: (
              <span><HistoryOutlined /> 审计日志</span>
            ),
            children: (
              <Card className={styles.tableCard} bordered={false}>
                <Spin spinning={auditLoading}>
                  {auditLogs.length === 0 && !auditLoading ? (
                    <Empty description="暂无审计日志" />
                  ) : (
                    <Table
                      columns={auditColumns}
                      dataSource={auditLogs}
                      rowKey="id"
                      pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条` }}
                      scroll={{ x: 900 }}
                    />
                  )}
                </Spin>
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={`编辑申领 #${editingRecord?.id || ''}`}
        open={editOpen}
        onCancel={closeEdit}
        footer={null}
        destroyOnClose
        centered
        width={480}
      >
        <RecordEditForm form={form} onFinish={handleSave} submitting={saving} />
      </Modal>

      <Modal
        title="操作详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={640}
      >
        <pre className={styles.detailPre}>{detailText}</pre>
      </Modal>
    </div>
  );
}
